import { useEffect, useMemo, useState } from 'react'
import {
  Activity,
  ArrowRight,
  Bell,
  Copy,
  Database,
  FolderTree,
  IdCard,
  Info,
  KeyRound,
  Lock,
  LogIn,
  MailPlus,
  Pencil,
  Plug,
  Plus,
  RefreshCw,
  ScrollText,
  Sliders,
  Smartphone,
  Tags,
  Trash2,
  UserPlus,
  Users,
  Server as ServerIcon,
} from 'lucide-react'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import ConfirmDialog from '../../components/ConfirmDialog'
import { InfoNote, Textarea } from '../../components/Form'
import IconButton from '../../components/IconButton'
import RowActions from '../../components/RowActions'
import Select from '../../components/Select'
import { DeniedState, EmptyState, ErrorState, LoadingState } from '../../components/States'
import StatusBadge from '../../components/StatusBadge'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import { serverDetail, serverText } from '../../lib/i18n'
import { strings as licenseStrings } from '../license/strings'
import DataSourceGroupsModal from './DataSourceGroupsModal'
import DataSourceMappingModal from './DataSourceMappingModal'
import DataSourceUsersModal from './DataSourceUsersModal'
import SettingsTabs from './SettingsTabs'
import { Block, Matrix, RadioList, ReadValue, Row, RowGroup, SwitchRow } from './SettingsParts'
import { ADVANCED_SECTIONS, advancedInitialForm, advancedKeys, isOn } from './advancedForm'
import { sectionIdsFor } from './settingsNav'
import {
  deleteDataSource,
  deleteFileServer,
  getAdvanced,
  saveDataSource,
  saveFileServer,
  saveSection,
  sourceName,
  testDirectorySource,
} from './settingsService'
import useSettingsForm, { useSection } from './useSettingsForm'
import { advancedStrings as S, common as C, settingsStrings } from './strings'
import './settings.css'

/* The sections this page opens, from the one map every settings page shares. */
const SECTION_IDS = sectionIdsFor('advanced')
const H = settingsStrings.hub
const P = S.sheet


const REGISTER_URL = '/Signup'
const GRAPH = 'https://graph.microsoft.com/v1.0/'

/** A blank Entra source — the kind most installations add now. */
const newSource = () => ({
  id: 0,
  name: '',
  type: 'ENTRA',
  connectionString: GRAPH,
  username: '',
  password: '',
  domain: '',
  filters: '',
  ssl: true,
  isActive: true,
  limitAccessToGroup: '',
  adminGroups: '',
  userResignedIfMissing: false,
  updateUserIdIfChangeDetected: false,
  resignedUsersAction: 'NoAction',
  detectResignedInData: false,
  resignedIdentification: '',
})

const newServer = () => ({ id: 0, serverId: '', serverAddress: '' })

/* The terms' "this is an update" box is a decision made per save, never a remembered
   state — ticking it makes everyone accept again — so a committed save puts it back. */
const afterSave = (group) => (group === 'terms' ? { termsUpdated: false } : undefined)

/** إعدادات متقدمة — Views/ProfileMapping/Index.cshtml. */
export default function AdvancedSettingsPage() {
  const toast = useToast()
  const nav = useSection('login', SECTION_IDS)
  const current = nav.current
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)

  const save = useMemo(() => (group, form) => saveSection(group, form, data), [data])
  const settings = useSettingsForm({ sections: ADVANCED_SECTIONS, save, afterSave })
  const { form, set, setValue, start } = settings

  useEffect(() => {
    getAdvanced()
      .then((response) => {
        setData(response)
        start(advancedInitialForm(response), advancedKeys(response))
      })
      .catch(setLoadError)
  }, [start])

  /* The record lists change the page's data without touching the form: re-reading must
     not throw away settings someone is halfway through editing. */
  const reloadRecords = async () => {
    const refreshed = await getAdvanced()
    setData((previous) => ({ ...previous, dataSources: refreshed.dataSources, servers: refreshed.servers }))
  }

  /** Attribute1..5 wear the name configured in the same page's custom-names block. */
  const fieldLabel = (field) => field.label ?? (data?.settings?.[field.setting] || field.name)

  const toggleField = (key, name) => {
    const current = form?.[key] ?? []
    setValue(key, current.includes(name) ? current.filter((item) => item !== name) : [...current, name])
  }

  /* The link is for pasting into an email, so it has to be the whole address. */
  const registerLink = new URL(REGISTER_URL, window.location.origin).href
  const copyRegisterLink = async () => {
    try {
      await navigator.clipboard.writeText(registerLink)
      toast.success(S.registration.copied)
    } catch {
      toast.error(S.registration.copyFailed)
    }
  }

  /* ---- records: data sources and servers, edited inside their own sheet ---- */

  const [source, setSource] = useState(null)
  const [server, setServer] = useState(null)
  const [recordBusy, setRecordBusy] = useState('')
  const [removing, setRemoving] = useState(null)
  /* A source opened to look inside: its users, its departments or its profile mapping. */
  const [inspecting, setInspecting] = useState(null)

  /* Leaving a record section leaves its editor too, so coming back starts at the list. */
  useEffect(() => {
    setSource(null)
    setServer(null)
  }, [current])

  const setSourceField = (key) => (value) => setSource((previous) => ({ ...previous, [key]: value }))
  const setSourceType = (type) =>
    setSource((previous) => ({
      ...previous,
      type,
      /* Switching the type moves the Graph address in or out with it. */
      connectionString:
        type === 'ENTRA'
          ? previous.connectionString || GRAPH
          : previous.connectionString === GRAPH
            ? ''
            : previous.connectionString,
      ssl: type === 'ENTRA' ? true : previous.ssl,
    }))

  const editSource = (row) =>
    setSource({
      id: row.id,
      name: row.englishName || row.arabicName,
      type: row.type,
      connectionString: row.connectionString,
      username: row.username,
      password: '',
      domain: row.domain,
      filters: row.filters,
      ssl: row.ssl,
      isActive: row.isActive,
      limitAccessToGroup: row.limitAccessToGroup,
      adminGroups: row.adminGroups,
      userResignedIfMissing: row.userResignedIfMissing,
      updateUserIdIfChangeDetected: row.updateUserIdIfChangeDetected,
      resignedUsersAction: row.resignedUsersAction,
      detectResignedInData: row.detectResignedInData,
      resignedIdentification: row.resignedIdentification,
    })

  const submitSource = async () => {
    setRecordBusy('save')
    const outcome = await saveDataSource(source)
    setRecordBusy('')
    if (!outcome.ok) {
      toast.error(serverDetail(outcome.message) || C.saveFailed)
      return
    }
    await reloadRecords()
    setSource(null)
    toast.success(outcome.message || C.saved)
  }

  /* Sends what is in the form, saved or not — so a source is never saved blind. */
  const testSource = async () => {
    setRecordBusy('test')
    const outcome = await testDirectorySource(source)
    setRecordBusy('')
    if (outcome.ok) toast.success(outcome.message)
    else toast.error(serverDetail(outcome.message) || C.saveFailed)
  }

  const submitServer = async () => {
    setRecordBusy('save')
    const outcome = await saveFileServer(server)
    setRecordBusy('')
    if (!outcome.ok) {
      toast.error(serverDetail(outcome.message) || C.saveFailed)
      return
    }
    await reloadRecords()
    setServer(null)
    toast.success(serverText(outcome.message) || C.saved)
  }

  const confirmRemove = async () => {
    const outcome =
      removing.kind === 'source' ? await deleteDataSource(removing.row.id) : await deleteFileServer(removing.row.id)
    if (!outcome.ok) {
      toast.error(serverDetail(outcome.message) || C.saveFailed)
      return
    }
    await reloadRecords()
    setRemoving(null)
    toast.success(serverText(outcome.message) || (removing.kind === 'source' ? S.sources.removed : S.servers.removed))
  }

  const groupOptions = (data?.groups ?? []).map((group) => ({ value: group.id, label: group.name }))
  const sourceOptions = [
    { value: 'None', label: C.none },
    ...([form?.deptsSource, form?.usersSource, form?.userFetchSource].some((value) => value === 'ActiveDirectory')
      ? [{ value: 'ActiveDirectory', label: S.sync.legacyLdap }] : []),
    ...(data?.dataSources ?? [])
      .filter((row) => row.isActive)
      .map((row) => ({ value: String(row.id), label: `${row.englishName || row.arabicName} (${row.type})` })),
  ]
  const s = data?.settings ?? {}

  /** A number field that wants only a few characters. */
  const number = (key, label, hint) => (
    <Row label={label} hint={hint}>
      {(fieldId) => (
        <TextField id={fieldId} inputMode="numeric" dir="ltr" value={form[key]} onChange={set(key)} />
      )}
    </Row>
  )

  /** A boolean setting, as a switch row. */
  const flag = (key, label) => (
    <SwitchRow label={label} checked={!!form[key]} onChange={set(key)} />
  )

  const section = (id) => {
    switch (id) {
      case 'login':
        return (
          <>
            <Block title={P.signInMethod} icon={KeyRound}>
              {/* The licence payload is encrypted in dbo.License, so its type is only read. */}
              <ReadValue label={S.login.licenseType}>
                {licenseStrings.licenseTypes[form.licenseType] || form.licenseType || '—'}
              </ReadValue>
              <Row label={S.login.authMethod}>
                {(fieldId) => (
                  <Select
                    id={fieldId}
                    value={form.authenticationId}
                    onChange={set('authenticationId')}
                    options={(data?.authMethods ?? []).map((method) => ({
                      value: method.id,
                      label: S.login.authMethodOptions?.[method.id] ?? method.name,
                    }))}
                  />
                )}
              </Row>
              <Row label={S.login.authSource}>
                {(fieldId) => (
                  <Select
                    id={fieldId}
                    value={form.authenticationMode}
                    onChange={set('authenticationMode')}
                    options={[
                      { value: 'Forms', label: S.login.loginPage },
                      { value: 'Windows', label: S.login.ldapSso },
                    ]}
                  />
                )}
              </Row>
              {/* Three answers that each take a sentence: a select cut all three off. */}
              <RowGroup label={S.login.usernameFormat}>
                {(labelId) => (
                  <RadioList
                    name="usernameFormat"
                    labelledBy={labelId}
                    value={form.usernameFormat}
                    onChange={set('usernameFormat')}
                    options={S.login.usernameFormatOptions}
                  />
                )}
              </RowGroup>
            </Block>

            <Block
              title={S.login.otp}
              icon={Smartphone}
              toggle={{ checked: !!form.otpEnabled, onChange: set('otpEnabled') }}
            >
              <Row label={S.login.otpTarget}>
                {(fieldId) => (
                  <Select id={fieldId} value={form.otpTarget} onChange={set('otpTarget')} options={S.login.otpTargets} />
                )}
              </Row>
              {number('otpLength', S.login.otpLength)}
              {number('otpValidPeriod', S.login.otpValidPeriod)}
            </Block>

            {/* The session timeout rides on the lockout's save action, but it is not part
                of the lockout — so it stays when the lockout is off. */}
            <Block title={P.lockout} icon={Lock}>
              {flag('lockoutEnabled', S.login.lockoutEnabled)}
              {form.lockoutEnabled && number('attempts', S.login.attempts)}
              {form.lockoutEnabled && number('lockoutWindow', S.login.lockoutWindow)}
              {number('sessionTimeout', S.login.sessionTimeout)}
            </Block>

            {/* A feature, so its switch rides in the head and the panel folds while it is off
                (2026-10-22) — two empty 6-line boxes under a switch that was off were the
                page's largest thing. On, the two languages sit side by side; the panel's
                title already says what they are. */}
            <Block
              title={P.terms}
              icon={ScrollText}
              toggle={{ checked: !!form.termsEnabled, onChange: set('termsEnabled') }}
            >
              <div className="set-media set-media--two">
                <Row stack label={H.inArabic}>
                  {(fieldId) => <Textarea id={fieldId} rows={5} dir="rtl" value={form.termsAr} onChange={set('termsAr')} />}
                </Row>
                <Row stack label={H.inEnglish}>
                  {(fieldId) => <Textarea id={fieldId} rows={5} dir="ltr" value={form.termsEn} onChange={set('termsEn')} />}
                </Row>
              </div>
              {flag('termsUpdated', S.login.termsUpdated)}
            </Block>
          </>
        )

      case 'registration':
        return (
          <>
            <Block title={P.accounts} icon={MailPlus}>
              {flag('activateInvitations', S.registration.activateInvitations)}
              {flag('activateResetPassword', S.registration.activateResetPassword)}
            </Block>
            <Block
              title={S.registration.showRegistrationLink}
              icon={UserPlus}
              toggle={{ checked: !!form.showRegistrationLink, onChange: set('showRegistrationLink') }}
            >
              <Row label={S.registration.activationMethod} hint={S.registration.noteBody}>
                {(fieldId) => (
                  <Select
                    id={fieldId}
                    value={form.activationBy}
                    onChange={set('activationBy')}
                    options={S.registration.activationOptions}
                  />
                )}
              </Row>
              {flag('emailOnDomain', S.registration.emailOnDomain)}
              <Row label={S.registration.domains}>
                {(fieldId) => (
                  <TextField
                    id={fieldId}
                    value={form.companyDomains}
                    onChange={set('companyDomains')}
                    placeholder="example.gov.sa"
                    dir="ltr"
                  />
                )}
              </Row>
              <Row label={P.registrationLink}>
                {(fieldId) => (
                  <TextField
                    id={fieldId}
                    value={registerLink}
                    readOnly
                    dir="ltr"
                    trailing={<IconButton icon={Copy} tone="primary" label={H.copy} onClick={copyRegisterLink} />}
                  />
                )}
              </Row>
            </Block>
          </>
        )

      case 'profile':
        return (
          <>
            <Block title={S.profile.title} icon={IdCard}>
              <Matrix
                caption={S.profile.title}
                head={P.field}
                columns={[
                  { id: 'visibleFields', label: P.visible },
                  { id: 'editableFields', label: P.editable },
                ]}
                rows={S.profile.fields.map((field, index) => ({
                  id: field.name,
                  label: fieldLabel(field),
                  locked: index === S.profile.lockedEditIndex,
                }))}
                cell={(row, column) => (
                  <Checkbox
                    id={`${column.id}-${row.id}`}
                    checked={(form[column.id] ?? []).includes(row.id)}
                    /* Username is never the user's to edit — the legacy locks it too. */
                    disabled={column.id === 'editableFields' && row.locked}
                    onChange={() => toggleField(column.id, row.id)}
                  >
                    <span className="sr-only">{`${row.label} — ${column.label}`}</span>
                  </Checkbox>
                )}
              />
            </Block>

            <Block title={P.customNames} icon={Tags}>
              <Matrix
                fields
                caption={P.customNames}
                head={S.profile.attribute}
                columns={[
                  { id: 'Ar', label: P.arabic },
                  { id: 'En', label: P.english },
                ]}
                rows={[1, 2, 3, 4, 5].map((index) => ({ id: index, label: String(index) }))}
                cell={(row, column) => (
                  <TextField
                    value={form[`attribute${row.id}${column.id}`]}
                    onChange={set(`attribute${row.id}${column.id}`)}
                    dir={column.id === 'Ar' ? 'rtl' : 'ltr'}
                    aria-label={`${S.profile.attribute} ${row.id} — ${column.label}`}
                  />
                )}
              />
            </Block>

            <Block title={P.afterSignIn} icon={LogIn}>
              {flag('allowProfileEditPage', P.openProfile)}
              {flag('forceGroupChange', S.profile.forceGroupLabel)}
              {form.forceGroupChange && (
                <Row label={S.profile.forceGroupSelect}>
                  {(fieldId) => (
                    <Select id={fieldId} value={form.forceGroupId} onChange={set('forceGroupId')} options={groupOptions} />
                  )}
                </Row>
              )}
            </Block>
          </>
        )

      case 'sources': {
        if (source) {
          const entra = source.type === 'ENTRA'
          return (
            <>
              <button type="button" className="set-back" onClick={() => setSource(null)}>
                <ArrowRight className="ltr-flip" size={16} strokeWidth={2} aria-hidden="true" />
                <bdi>{H.backToList}</bdi>
              </button>
              <Block title={source.id ? source.name || S.sources.edit : S.sources.newSource} icon={Plug}>
                <Row label={S.sources.type}>
                  {(fieldId) => (
                    <Select
                      id={fieldId}
                      value={source.type}
                      onChange={setSourceType}
                      options={[
                        { value: 'LDAP', label: 'LDAP / LDAPS' },
                        { value: 'ENTRA', label: 'Microsoft Entra ID' },
                        /* A source of an older type (WebAPI) is named for what it is. It
                           cannot be saved as such, and can be deleted from the list. */
                        ...(source.type && !['LDAP', 'ENTRA'].includes(source.type)
                          ? [{ value: source.type, label: `${source.type} (${S.sources.legacyType})` }]
                          : []),
                      ]}
                    />
                  )}
                </Row>
                <Row label={S.sources.name}>
                  {(fieldId) => <TextField id={fieldId} value={source.name} onChange={setSourceField('name')} />}
                </Row>
                <Row label={entra ? S.sources.tenantId : S.sources.domain} hint={entra ? undefined : S.sources.loginDomainHint}>
                  {(fieldId) => <TextField id={fieldId} value={source.domain} onChange={setSourceField('domain')} dir="ltr" />}
                </Row>
                <Row label={entra ? S.sources.clientId : S.sources.username} hint={entra ? undefined : S.sources.bindUsernameHint}>
                  {(fieldId) => (
                    <TextField id={fieldId} value={source.username} onChange={setSourceField('username')} dir="ltr" />
                  )}
                </Row>
                <Row
                  label={entra ? S.sources.clientSecret : S.sources.password}
                  hint={source.id ? S.sources.secretUnchanged : undefined}
                >
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      type="password"
                      value={source.password}
                      onChange={setSourceField('password')}
                      dir="ltr"
                    />
                  )}
                </Row>
                <Row label={entra ? S.sources.graphEndpoint : S.sources.connectionString}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      value={source.connectionString}
                      onChange={setSourceField('connectionString')}
                      dir="ltr"
                    />
                  )}
                </Row>
                <Row label={entra ? S.sources.odataFilter : S.sources.ldapFilter}>
                  {(fieldId) => <TextField id={fieldId} value={source.filters} onChange={setSourceField('filters')} dir="ltr" />}
                </Row>
                <Row label={S.sources.accessGroup}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      value={source.limitAccessToGroup}
                      onChange={setSourceField('limitAccessToGroup')}
                      dir="ltr"
                    />
                  )}
                </Row>
                <Row label={S.sources.adminGroups} hint={S.sources.groupsNote}>
                  {(fieldId) => (
                    <TextField id={fieldId} value={source.adminGroups} onChange={setSourceField('adminGroups')} dir="ltr" />
                  )}
                </Row>
                {entra && <InfoNote>{S.sources.entraPrerequisites}</InfoNote>}
                {/* As the legacy form: what happens to a resigned user, then how one is told —
                    no longer returned, or marked in the returned data. No action needs neither. */}
                <Row label={S.sources.resignedAction}>
                  {(fieldId) => (
                    <Select
                      id={fieldId}
                      value={source.resignedUsersAction}
                      onChange={setSourceField('resignedUsersAction')}
                      options={[
                        { value: 'NoAction', label: S.sources.resignedActionNone },
                        { value: 'AutoHide', label: S.sources.resignedActionHide },
                        { value: 'ByAdmin', label: S.sources.resignedActionAdmin },
                      ]}
                    />
                  )}
                </Row>
                {source.resignedUsersAction !== 'NoAction' && (
                  <>
                    <SwitchRow label={S.sources.resignMissing} hint={S.sources.resignMissingHint}
                      checked={source.userResignedIfMissing} onChange={setSourceField('userResignedIfMissing')} />
                    <SwitchRow label={S.sources.resignInData} hint={S.sources.resignInDataHint}
                      checked={source.detectResignedInData} onChange={setSourceField('detectResignedInData')} />
                    {source.detectResignedInData && (
                      <Row label={S.sources.resignedVariable} hint={S.sources.resignedVariableHint}>
                        {(fieldId) => (
                          <TextField
                            id={fieldId}
                            value={source.resignedIdentification}
                            onChange={setSourceField('resignedIdentification')}
                            placeholder="Key=Value"
                            dir="ltr"
                          />
                        )}
                      </Row>
                    )}
                  </>
                )}
                {!entra && <SwitchRow label={S.sources.detectUsernameChange} hint={S.sources.detectUsernameChangeHint}
                  checked={source.updateUserIdIfChangeDetected} onChange={setSourceField('updateUserIdIfChangeDetected')} />}
                {!entra && (
                  <SwitchRow label={S.sources.ssl} checked={source.ssl} onChange={setSourceField('ssl')} />
                )}
                <SwitchRow label={S.sources.isActive} checked={source.isActive} onChange={setSourceField('isActive')} />
              </Block>
            </>
          )
        }
        return (data?.dataSources ?? []).length === 0 ? (
          <Block>
            <EmptyState icon={Plug} title={P.sourcesEmpty} />
          </Block>
        ) : (
          <ul className="set-list">
            {data.dataSources.map((row) => (
              <li key={row.id} className="set-list__row">
                <span className="set-kind" dir="ltr">
                  {row.type || '—'}
                </span>
                <span className="set-list__text">
                  <span className="set-list__name">
                    <bdi>{sourceName(row)}</bdi>
                  </span>
                  {/* The address reads left to right, but the LINE follows the page: forcing
                      the whole line `ltr` put it against the far edge in Arabic, away from
                      the name above it and beside the status badge. */}
                  <span className="set-list__sub">
                    <bdi dir="ltr">{row.connectionString || row.domain}</bdi>
                  </span>
                </span>
                <StatusBadge tone={row.isActive ? 'success' : 'dark'}>{row.isActive ? C.active : C.inactive}</StatusBadge>
                {/* The app's row actions (2026-10-22): the same soft chips in their tones as
                    every table, where these were five bare grey glyphs. All five stay out. */}
                <RowActions
                  inline={5}
                  actions={[
                    {
                      id: 'mapping',
                      icon: Sliders,
                      label: S.sources.profileMapping,
                      onClick: () => setInspecting({ kind: 'mapping', row }),
                    },
                    {
                      id: 'users',
                      icon: Users,
                      label: S.sources.browseUsers,
                      onClick: () => setInspecting({ kind: 'users', row }),
                    },
                    {
                      id: 'groups',
                      icon: FolderTree,
                      label: S.sources.browseGroups,
                      onClick: () => setInspecting({ kind: 'groups', row }),
                    },
                    { id: 'edit', icon: Pencil, label: S.sources.edit, onClick: () => editSource(row) },
                    {
                      id: 'remove',
                      icon: Trash2,
                      danger: true,
                      label: S.sources.remove,
                      onClick: () => setRemoving({ kind: 'source', row }),
                    },
                  ]}
                />
              </li>
            ))}
          </ul>
        )
      }

      case 'sync':
        return (
          <>
            <Block title={P.syncSources} icon={Database}>
              <Matrix
                fields
                caption={P.syncSources}
                head={P.dataset}
                columns={[
                  { id: 'source', label: P.source },
                  { id: 'method', label: P.method },
                ]}
                rows={[
                  { id: 'userInfo', label: P.userInfo, source: 'userFetchSource' },
                  {
                    id: 'depts',
                    label: P.depts,
                    source: 'deptsSource',
                    // Department discovery always imports and synchronizes; it has no method setting.
                  },
                  {
                    id: 'users',
                    label: P.allUsers,
                    source: 'usersSource',
                    method: 'usersType',
                    methods: [
                      { value: 'Import', label: S.sync.import },
                      { value: 'Sync', label: S.sync.syncOnly },
                      { value: 'ImportSync', label: S.sync.importSync },
                    ],
                  },
                ]}
                cell={(row, column) =>
                  column.id === 'source' ? (
                    <Select
                      value={form[row.source]}
                      onChange={set(row.source)}
                      options={sourceOptions}
                      aria-label={`${row.label} — ${P.source}`}
                    />
                  ) : row.method ? (
                    <Select
                      value={form[row.method]}
                      onChange={set(row.method)}
                      options={row.methods}
                      aria-label={`${row.label} — ${P.method}`}
                    />
                  ) : (
                    <span>{row.id === 'userInfo' ? P.readProfile : S.sync.importSync}</span>
                  )
                }
              />
            </Block>

            <Block title={P.autoSync} icon={RefreshCw}>
              {flag('deptsAutoFetch', S.sync.deptsAutoFetch)}
              {form.deptsAutoFetch && (
                <Row label={S.sync.delay}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      dir="ltr"
                      value={form.deptsAutoFetchInterval}
                      onChange={set('deptsAutoFetchInterval')}
                    />
                  )}
                </Row>
              )}
              {flag('usersAutoFetch', S.sync.usersAutoFetch)}
              {form.usersAutoFetch && (
                <Row label={S.sync.delay}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      dir="ltr"
                      value={form.usersAutoFetchInterval}
                      onChange={set('usersAutoFetchInterval')}
                    />
                  )}
                </Row>
              )}
            </Block>

            {/* Editable: the sync policies are saved with the rest of the section (dev, a7bfff8ec).
                Written as rows, like every other setting on the page. */}
            <Block title={S.sync.adIntegration} icon={Sliders}>
              <Row label={S.sync.overLicense}>
                {(fieldId) => (
                  <Select
                    id={fieldId}
                    value={form.whenFetchedUsersCountGreaterThanLicense}
                    onChange={set('whenFetchedUsersCountGreaterThanLicense')}
                    options={S.sync.overLicenseOptions}
                  />
                )}
              </Row>
              {flag('deleteAdGroupsWithNoUsers', S.sync.deleteEmptyGroups)}
            </Block>
          </>
        )

      case 'servers':
        if (server) {
          return (
            <>
              <button type="button" className="set-back" onClick={() => setServer(null)}>
                <ArrowRight className="ltr-flip" size={16} strokeWidth={2} aria-hidden="true" />
                <bdi>{H.backToList}</bdi>
              </button>
              <Block title={server.id ? S.servers.edit : S.servers.newServer} icon={ServerIcon}>
                <Row label={S.servers.serverId}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      inputMode="numeric"
                      dir="ltr"
                      value={server.serverId}
                      onChange={(value) => setServer((previous) => ({ ...previous, serverId: value }))}
                    />
                  )}
                </Row>
                <Row label={S.servers.serverUrl}>
                  {(fieldId) => (
                    <TextField
                      id={fieldId}
                      dir="ltr"
                      placeholder="https://"
                      value={server.serverAddress}
                      onChange={(value) => setServer((previous) => ({ ...previous, serverAddress: value }))}
                    />
                  )}
                </Row>
              </Block>
            </>
          )
        }
        return (data?.servers ?? []).length === 0 ? (
          <Block>
            <EmptyState icon={ServerIcon} title={P.serversEmpty} />
          </Block>
        ) : (
          <ul className="set-list">
            {data.servers.map((row) => (
              <li key={row.id} className="set-list__row">
                <span className="set-kind" dir="ltr">
                  #{row.serverId}
                </span>
                <span className="set-list__text">
                  <span className="set-list__sub" dir="ltr">
                    {row.serverAddress}
                  </span>
                </span>
                <RowActions
                  actions={[
                    {
                      id: 'edit',
                      icon: Pencil,
                      label: S.servers.edit,
                      onClick: () =>
                        setServer({ id: row.id, serverId: String(row.serverId), serverAddress: row.serverAddress }),
                    },
                    {
                      id: 'remove',
                      icon: Trash2,
                      danger: true,
                      label: S.servers.remove,
                      onClick: () => setRemoving({ kind: 'server', row }),
                    },
                  ]}
                />
              </li>
            ))}
          </ul>
        )

      case 'services':
        return (
          <>
            <Block title={P.notificationService} icon={Bell}>
              {flag('ServiceEnabledNotifications', S.services.enableNotificationService)}
              {number('MaxRetries', S.services.maxRetries, S.services.maxRetriesHint)}
              {number('MaxTaskExecutionMinutes', S.services.maxTaskMinutes, S.services.maxTaskMinutesHint)}
            </Block>

            {/* Every background service's state, as stored. These were fields that opened
                on invented defaults and saved nothing — the save action takes the three
                settings above and no others (COSMETIC_UI.md). */}
            <Block title={P.serviceStatus} icon={Activity}>
              <div className="set-matrix-wrap">
                <table className="set-matrix set-matrix--fields">
                  <caption className="sr-only">{P.serviceStatus}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{P.service}</th>
                      <th scope="col">{C.status}</th>
                      <th scope="col">{P.interval}</th>
                    </tr>
                  </thead>
                  {S.services.groups.map((family) => (
                    <tbody key={family.id}>
                      <tr className="set-matrix__group">
                        <th scope="rowgroup" colSpan={3}>
                          <bdi>{S.services[family.id]}</bdi>
                        </th>
                      </tr>
                      {family.items.map((service) => {
                        const running = isOn(s[`ServiceEnabled${service.id}`])
                        return (
                          <tr key={service.id}>
                            <th scope="row">
                              <bdi>{service.name}</bdi>
                            </th>
                            <td>
                              <StatusBadge tone={running ? 'success' : 'dark'}>{running ? P.running : P.stopped}</StatusBadge>
                            </td>
                            <td>
                              <span className="set-mono" dir="ltr">
                                {s[`ServiceDelay${service.id}`] || '—'}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  ))}
                </table>
              </div>
              <p className="set-note">
                <Info size={14} strokeWidth={2} aria-hidden="true" />
                <bdi>{H.storedOnly}</bdi>
              </p>
            </Block>
          </>
        )

      default:
        return null
    }
  }

  const saves = Boolean(ADVANCED_SECTIONS[current])

  /* A record sheet's footer follows its view: the list offers to add one, the editor
     saves the one being edited. */
  const recordFooter =
    current === 'sources' ? (
      source ? (
        <>
          <Button onClick={submitSource} busy={recordBusy === 'save'} disabled={recordBusy !== ''}>
            {C.save}
          </Button>
          <Button variant="soft" onClick={testSource} busy={recordBusy === 'test'} disabled={recordBusy !== ''}>
            {S.sources.test}
          </Button>
          <Button variant="ghost" onClick={() => setSource(null)}>
            {C.reset}
          </Button>
        </>
      ) : (
        <>
          <Button onClick={() => setSource(newSource())}>
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            {S.sources.newSource}
          </Button>
        </>
      )
    ) : current === 'servers' ? (
      server ? (
        <>
          <Button onClick={submitServer} busy={recordBusy === 'save'}>
            {C.save}
          </Button>
          <Button variant="ghost" onClick={() => setServer(null)}>
            {C.reset}
          </Button>
        </>
      ) : (
        <>
          <Button onClick={() => setServer(newServer())}>
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            {S.servers.newServer}
          </Button>
        </>
      )
    ) : undefined

  return (
    <SettingsTabs
      page="advanced"
      title={S.title}
      current={current}
      onLocal={nav.select}
      dirty={saves && settings.isDirty(current)}
      saving={settings.saving}
      onSave={saves ? () => settings.saveSection(current) : undefined}
      onDiscard={() => settings.resetSection(current)}
      focus={nav.focus}
      ready={settings.ready}
      footer={recordFooter}
      blocked={
        /* A REFUSED LOAD IS NOT A SLOW ONE — see SettingsPage. */
        loadError ? (
          loadError.denied ? (
            <DeniedState description={loadError.message} />
          ) : (
            <ErrorState title={C.loadFailed} description={loadError.message} />
          )
        ) : null
      }
    >
      {settings.ready ? section(current) : <LoadingState />}

      <ConfirmDialog
        open={Boolean(removing)}
        message={removing?.kind === 'server' ? S.servers.removeConfirm : S.sources.removeConfirm}
        confirmLabel={removing?.kind === 'server' ? S.servers.remove : S.sources.remove}
        cancelLabel={C.reset}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />

      {inspecting?.kind === 'users' && (
        <DataSourceUsersModal source={inspecting.row} onClose={() => setInspecting(null)} />
      )}
      {inspecting?.kind === 'groups' && (
        <DataSourceGroupsModal source={inspecting.row} onClose={() => setInspecting(null)} />
      )}
      {inspecting?.kind === 'mapping' && (
        <DataSourceMappingModal
          source={inspecting.row}
          onClose={() => setInspecting(null)}
          onSaved={reloadRecords}
        />
      )}
    </SettingsTabs>
  )
}
